import { Chip } from './chip';
import { Icon } from './icon';
import type { DocProcessingStatus } from '../../../../backend/src/matter/doc/doc.entity';
import { RunStatus } from '../../../../backend/src/types/types';
import { CHIP_COLOURS } from '../lib/chip-colours';
import { isProcessingStatus } from '../lib/pipeline-status';
import { cn } from '../lib/utils';
// We only need these fields to determine the processed state
interface DocWithProcessingState {
  isProcessed: boolean;
  status?: DocProcessingStatus | string | null;
  run?: { status: string | null } | null;
}

export enum ProcessingState {
  Processing = 'processing',
  Processed = 'processed',
  NotProcessed = 'not-processed',
  Staged = 'staged',
  Complete = 'complete',
  Failed = 'failed',
  Cancelled = 'cancelled',
}
interface ProcessingChipProps {
  processingState: ProcessingState;
  /** Overrides the default label for the given state. */
  processingText?: string;
  className?: string;
  showIcon?: boolean;
}

type ProcessingStateConfig = {
  chipClass: string;
  iconClass: string;
  defaultLabel: string;
};

const PROCESSING_CONFIG: Record<ProcessingState, ProcessingStateConfig> = {
  [ProcessingState.Processing]: {
    chipClass: CHIP_COLOURS.processing,
    iconClass: 'lucide--loader-2 animate-spin duration-1500',
    defaultLabel: 'Processing',
  },
  [ProcessingState.Processed]: {
    chipClass: CHIP_COLOURS.default,
    iconClass: 'lucide--circle-check text-teal-500',
    defaultLabel: 'Processed',
  },
  [ProcessingState.NotProcessed]: {
    chipClass: CHIP_COLOURS.default,
    iconClass: 'lucide--circle-x text-red-500',
    defaultLabel: 'Not processed',
  },
  [ProcessingState.Staged]: {
    chipClass: CHIP_COLOURS.default,
    iconClass: 'lucide--clock text-gray-500',
    defaultLabel: 'Awaiting submission',
  },
  [ProcessingState.Complete]: {
    chipClass: 'bg-green-50 text-green-700 border-green-200',
    iconClass: 'lucide--circle-check text-green-600',
    defaultLabel: 'Complete',
  },
  [ProcessingState.Failed]: {
    chipClass: 'bg-red-50 text-red-700 border-red-200',
    iconClass: 'lucide--circle-x text-red-600',
    defaultLabel: 'Failed',
  },
  [ProcessingState.Cancelled]: {
    chipClass: 'bg-amber-50 text-amber-700 border-amber-200',
    iconClass: 'lucide--circle-slash text-amber-600',
    defaultLabel: 'Cancelled',
  },
};

export function getProcessingState(doc: DocWithProcessingState): ProcessingState {
  switch (doc.status) {
    case 'completed':
      return ProcessingState.Processed;
    case 'failed':
      return ProcessingState.NotProcessed;
    case 'processing':
      return doc.run?.status && !isProcessingStatus(doc.run.status as RunStatus)
        ? ProcessingState.NotProcessed
        : ProcessingState.Processing;
    case 'pending':
      return ProcessingState.Staged;
    default: {
      const isCurrentlyProcessing =
        doc.run && doc.run.status && !doc.isProcessed && isProcessingStatus(doc.run.status as RunStatus);
      return isCurrentlyProcessing
        ? ProcessingState.Processing
        : doc.isProcessed
          ? ProcessingState.Processed
          : ProcessingState.NotProcessed;
    }
  }
}

export function ProcessingChip({ processingState, processingText, className, showIcon = true }: ProcessingChipProps) {
  const stateConfig = PROCESSING_CONFIG[processingState];
  const label = processingText ?? stateConfig.defaultLabel;

  return (
    <Chip className={cn(stateConfig.chipClass, 'w-fit border', className)}>
      {showIcon && <Icon className={stateConfig.iconClass} />}
      <span>{label}</span>
    </Chip>
  );
}
